import { Component, DestroyRef, OnDestroy, ElementRef, Input, inject } from "@angular/core";
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { skip, Subscription } from 'rxjs';
import { NursingAuditService } from 'src/app/services/nursing-audit.service';
import { ThemeColorService } from 'src/app/services/theme-color.service';
import { ThemePreferenceService } from 'src/app/services/theme-preference.service';
import Chart from 'chart.js/auto'

// The API uses this color to identify points entered by the reviewed user.
const REVIEWED_USER_API_COLOR = '#FCD581';

@Component({
    selector: "app-pain-score-charts",
    templateUrl: "./pain-score-charts.component.html",
    styleUrls: ["./pain-score-charts.component.css"],
    standalone: false
})
export class PainScoreCharts implements OnDestroy {
  private nursingAuditService = inject(NursingAuditService);
  private elementRef = inject(ElementRef);
  private themeColorService = inject(ThemeColorService);
  private themePreferenceService = inject(ThemePreferenceService);
  private destroyRef = inject(DestroyRef);

  selectedRow: any = {};
    data: any = [];
    curAdmin!: string;
    patientList: any;
    showCharts = false;
    loading = false;
    medGroup = '';
    admin = '';
    p2pModule = '';
    painScoreInfo!: boolean;
    @Input() deId = false;
    private chartMap = new Map<string, Chart>();
    private dataCache = new Map<string, any>();
    private activeSubscription: Subscription | null = null;
    private currentRowKey: string | null = null;

    constructor() {
      this.themePreferenceService.preference$
        .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.refreshChartTheme());
    }

    private refreshChartTheme(): void {
      if (!this.data?.datasets) {
        return;
      }

      this.setPatientData(this.data);
      const legendColor = this.themeColorService.resolve('--chart-pain-legend-text');
      const axisColor = this.themeColorService.resolve('--chart-pain-axis-text');
      const gridColor = this.themeColorService.resolve('--chart-pain-grid-line');

      this.data.datasets.forEach((patient: any) => {
        const chart = this.chartMap.get(patient.id);
        if (!chart) {
          return;
        }

        chart.data = patient.chartData;
        const options = chart.options as any;
        options.plugins.legend.labels.color = legendColor;
        options.scales.x.ticks.color = axisColor;
        options.scales.x.grid.color = gridColor;
        options.scales.y.ticks.color = axisColor;
        options.scales.y.grid.color = gridColor;
        chart.update();
      });
    }

    ngOnDestroy(): void {
      this.activeSubscription?.unsubscribe();
      this.destroyCharts();
    }

    private destroyCharts(): void {
      this.chartMap.forEach(chart => chart.destroy());
      this.chartMap.clear();
    }

    private createCharts(): void {
      if (!this.data?.datasets) return;

      this.data.datasets.forEach((patient: any) => {
        if (this.chartMap.has(patient.id)) return;

        const canvas = this.elementRef.nativeElement.querySelector(`#${patient.id}`);
        if (!canvas) return;

        const ids = patient.painData.map((row: any) => row['adminId']);
        const dates = patient.painData.map((row: any) => this.getFullLabelString(row['takenTime']));

        const chart = new Chart(canvas, {
          type: 'line',
          data: patient.chartData,
          options: {
            plugins: {
              legend: {
                display: false,
                labels: { color: this.themeColorService.resolve('--chart-pain-legend-text') }
              },
              tooltip: {
                enabled: true,
                callbacks: {
                  title: function(tooltipItems) {
                    const tt = ids[tooltipItems[0].dataIndex];
                    return 'Entered By:\n' + tt;
                  },
                  label: function() { return ''; },
                  footer: function(tooltipItems) {
                    return 'Entry Time:\n' + dates[tooltipItems[0].dataIndex];
                  }
                }
              }
            },
            scales: {
              x: {
                ticks: { color: this.themeColorService.resolve('--chart-pain-axis-text'), maxRotation: 45, minRotation: 45 },
                grid: { color: this.themeColorService.resolve('--chart-pain-grid-line') }
              },
              y: {
                ticks: { color: this.themeColorService.resolve('--chart-pain-axis-text'), stepSize: 1 },
                grid: { color: this.themeColorService.resolve('--chart-pain-grid-line') },
                min: 0,
                max: 10
              }
            },
            backgroundColor: 'transparent'
          }
        });
        this.chartMap.set(patient.id, chart);
      });
    }

    // Builds key for a current row to cache API response data in this.dataCache
    private getRowKey(row: any): string {
      return `${row['adminId']}_${row['p2pMedId']}_${row['transactionType']}`;
    }

    loadAllPatientsData(row: any) {
      const rowKey = this.getRowKey(row);

      // Same row already loaded and charts exist
      if (rowKey === this.currentRowKey && this.chartMap.size > 0) {
        return;
      }

      // Cancel any in-flight request
      this.activeSubscription?.unsubscribe();
      this.activeSubscription = null;

      this.destroyCharts();
      this.loading = true;
      this.data = []
      this.admin = row['adminName']
      this.p2pModule = row['transactionType']
      this.medGroup = row['medGroupName']
      this.currentRowKey = rowKey;

      // Use cached API response if available
      if (this.dataCache.has(rowKey)) {
        this.handleDataResponse(this.dataCache.get(rowKey), row);
        return;
      }

      const filter = {'adminId':row['adminId'],'p2pMedId':row['p2pMedId'],"transactionType":row['transactionType']}
      this.activeSubscription = this.nursingAuditService.getPainScoreData(filter).subscribe((res) => {
        this.activeSubscription = null;
        this.dataCache.set(rowKey, res);
        this.handleDataResponse(res, row);
      });
    }

    private handleDataResponse(res: any, row: any): void {
      this.data = res;
      this.curAdmin = row['adminId'];
      if (this.data.datasets && this.data.datasets.length > 0) {
        this.setPatientData(this.data);
        this.patientList = this.data.mrnList;
        this.showCharts = true;
        this.loading = false;
        setTimeout(() => this.createCharts());
      } else {
        this.loading = false;
        this.showCharts = false;
      }
    }
    
    setPatientData(data: any) {
      data.datasets.forEach((patient: any) => {
        const entries = patient.painData.map((row: any) => {return + row['painScore']})
        const dates = patient.painData.map((row: any) => {return this.getLabelString(row['takenTime'])})
        const bgColors = patient.painData.map((row: any) => {return row['bgColor']})
        const pointSizes = patient.painData.map((row: any) => {return row['pointSize']})
        const hoverSizes = patient.painData.map((row: any) => {return row['hoverSize']})
        const pointStyles = patient.painData.map((row: any) => {
          return row['bgColor'] && row['bgColor'].toUpperCase() === REVIEWED_USER_API_COLOR ? 'rect' : 'circle';
        });
        patient.id = "_".concat(patient.mrn)
        patient.chartData = {
          labels: dates,
          datasets: [
            {
              label:
                patient.mrn +
                " - " +
                patient.mrn,
              yAxisID: 'y',
              data: entries,
              pointBackgroundColor: bgColors,
              pointRadius: pointSizes,
              pointHoverRadius: hoverSizes,
              pointStyle: pointStyles,
              fill: false,
              borderColor: this.themeColorService.resolve('--chart-pain-line'),
              tension: 0.2,
              spanGaps: false
            },
          ]
        }
    })}
    
    getLabelString(date: any) {
      date = new Date(date)
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return [
        `${hours}:${minutes}`,
        `(${date.getMonth()+1}/${date.getDate()}/${date.getFullYear()})`
      ];
    }

    customTooltip(data: any) {
      return {enabled:true, callbacks: {
        title: () => "Entered By:",
        afterTitle: (context: any) => data[context.dataIndex.adminId],
      }}
    }

    getFullLabelString(date: any) {
      date = new Date(date)
      let dateStr = date.getMonth()+1 + "/" + date.getDate() + "/" + date.getFullYear() + " "
      
      if (date.getHours() < 10) {
          dateStr = dateStr + '0' + date.getHours()+ ':'
      }
      else {
        dateStr = dateStr + date.getHours() + ':'
      }
      if (date.getMinutes() < 10) {
        dateStr = dateStr + '0' + date.getMinutes()
      }
      else {
        dateStr = dateStr + date.getMinutes()
      }
      return dateStr
    }

  isDeId(name: any) {
    if(this.deId)
      return "xxxxxx xxxxxx"
    else
      return name
    }

    showPainScoreInfo() {
      this.painScoreInfo = true;
    }

    formatName(name: string): string {
    if (!name) return name;
    let formattedName = name.replace(/,\s*/g, ', ');
    
    // Capitalize first letter of each word
    formattedName = formattedName.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
    
    return formattedName;
  }

}
